import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { ChevronDown, ChevronUp, TableFiles, FilePdf, InvalidFile } from '@/assets/icons'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Link } from '@/components/link/Link'
import { DeletedFileBadge } from '@/matter-asset-common/files/components/DeletedFileBadge'
import { useDeletedFileIds } from '@/matter-asset-common/files/hooks/useDeletedFileIds'
import { matterPaths } from '@/routing/paths'
import { TimelineAssetTabs } from '../../constants'
import { selectTimelineAssetFiles } from '../../store/selectors'
import { TimelineAssetActions } from '../../store/slice'

/**
 * Reference list of the files already in the timeline, collapsed unless `defaultOpen` is set
 * ("Included in timeline"). Count comes from the asset overview's total_files;
 * the roster is lazy-loaded the first time the list is open.
 */
type IncludedInTimelineDrawerProps = {
  assetId: string
  totalFiles: number
  /** Initial expanded state only; the drawer owns it from then on. Lets stories render the open state directly. */
  defaultOpen?: boolean
}

export function IncludedInTimelineDrawer({ assetId, totalFiles, defaultOpen = false }: IncludedInTimelineDrawerProps) {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(defaultOpen)
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const files = useAppSelector((state: RootState) => selectTimelineAssetFiles(state, { assetId }))
  const { deletedFileIds } = useDeletedFileIds(files?.map((file) => file.user_file_id) ?? [])

  // Keyed on `open` rather than the click, so a drawer opened via `defaultOpen` still loads.
  useEffect(() => {
    if (open && !files) dispatch(TimelineAssetActions.refreshAssetData({ assetId }))
  }, [open, files, assetId, dispatch])

  if (totalFiles <= 0) return null

  const toggle = () => setOpen((wasOpen) => !wasOpen)

  const Chevron = open ? ChevronUp : ChevronDown
  return (
    <div className="border-y border-brand-neutral-200">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between py-1.5 min-h-[48px]" aria-expanded={open}>
        <div className="flex items-center gap-2.5">
          <TableFiles className="h-6 w-6 shrink-0" />
          <span className="text-base font-semibold text-brand-neutral-950">Included in timeline</span>
          <span className="inline-flex items-center justify-center min-w-[18px] h-[22px] px-1.5 rounded-full bg-brand-neutral-200 text-sm font-semibold text-brand-neutral-700">
            {totalFiles}
          </span>
        </div>
        <span className="flex items-center justify-center p-0.5 rounded-full border border-brand-neutral-300 bg-brand-neutral-200">
          <Chevron className="w-4 h-4 text-brand-neutral-700" />
        </span>
      </button>
      {open && (
        <div className="pb-3 pl-3">
          <div className="flex w-full flex-col items-start gap-1 border-l-2 border-brand-neutral-200 pl-3">
            <InlineMessage variant="info" icon={null} className="w-full px-2.5 py-2 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-800">
              Files already in this timeline won&apos;t be reprocessed unless a newer version is detected. To remove a file, go to{' '}
              {/* Opens in a new tab so staged uploads on this page are not lost to navigation. */}
              {activeMatterId ? (
                <Link href={matterPaths.timeline.tab(activeMatterId, TimelineAssetTabs.SOURCE_FILES)} size="sm" target="_blank" rel="noopener noreferrer">
                  Source Files
                </Link>
              ) : (
                'Source Files'
              )}
              .
            </InlineMessage>
            {files === null ? (
              <p className="pl-2 text-xs text-brand-neutral-500">Loading…</p>
            ) : files.length === 0 ? (
              <p className="pl-2 text-xs text-brand-neutral-500">No files yet.</p>
            ) : (
              files.map((file) => {
                const isDeleted = deletedFileIds.has(file.user_file_id)

                return (
                  <div key={file.user_file_id} className="flex w-full items-center gap-2 py-2 pl-2">
                    {isDeleted ? (
                      <InvalidFile className="h-5 w-5 shrink-0 text-true-gray-300" aria-hidden />
                    ) : (
                      <FilePdf className="h-5 w-5 shrink-0 text-brand-neutral-800" aria-hidden />
                    )}
                    <p
                      className={`min-w-0 flex-1 truncate text-sm leading-5 tracking-[-0.15px] ${isDeleted ? 'text-true-gray-500' : 'text-brand-neutral-950'}`}
                      title={file.display_name}
                    >
                      {file.display_name}
                    </p>
                    {isDeleted && <DeletedFileBadge />}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
